import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "maxLength", "characterCountFrom", "onChange"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMergeRefs } from 'flow/hooks/useMergeRefs';
import BaseInput from './BaseInput';
import CharacterCount from './CharacterCount';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A {@link BaseInput} that reveals a `used/max` counter near its limit. The
 * counter measures the same UTF-16 code units `maxLength` enforces, so the two
 * can never disagree.
 *
 * Like `BaseInput`, this must never write `value={...}` — it would break
 * uncontrolled callers whose value arrives after mount. `value` stays in
 * `props` and reaches the input through the spread; the effect below reads
 * `props.value` without destructuring it.
 */
var CharacterCountInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    maxLength = _ref.maxLength,
    characterCountFrom = _ref.characterCountFrom,
    onChange = _ref.onChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var inputRef = useRef(null);
  var setRefs = useMergeRefs(ref, inputRef);
  var characterCountId = "".concat(useId(), "-character-count");

  // `null` means "below the threshold, nothing to show". Re-rendering is
  // then skipped for free: every keystroke under the threshold sets
  // `null` over `null`, which React bails out of.
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    lengthToShow = _useState2[0],
    setLengthToShow = _useState2[1];
  var trackLength = function trackLength(next) {
    return setLengthToShow(next >= characterCountFrom ? next : null);
  };

  // Read the length off the element rather than off a prop: the field is
  // uncontrolled in some call sites, and its value can still arrive late
  // (a `defaultValue` fetched after mount).
  useEffect(function () {
    var _inputRef$current$val, _inputRef$current;
    trackLength((_inputRef$current$val = (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.value.length) !== null && _inputRef$current$val !== void 0 ? _inputRef$current$val : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.defaultValue]);
  var handleChange = function handleChange(event) {
    trackLength(event.target.value.length);
    onChange === null || onChange === void 0 || onChange(event);
  };
  var showCharacterCount = lengthToShow !== null;
  return /*#__PURE__*/_jsx(BaseInput
  // Spread first: `value` must stay absent unless the caller
  // passed one, and everything below is ours to own.
  , _objectSpread(_objectSpread({}, props), {}, {
    ref: setRefs,
    maxLength: maxLength
    // Reserve room so the counter never sits on top of the text.
    // `120/120` is ~48px at text-xs, plus the pe-3 inset.
    ,
    className: twMerge(className, showCharacterCount && 'pe-18'),
    "aria-describedby": showCharacterCount ? characterCountId : undefined
    // A value saved before the limit existed can still be longer
    // than `maxLength`, which only constrains typing.
    ,
    "aria-invalid": showCharacterCount && lengthToShow > maxLength ? true : undefined,
    trailingSlot: showCharacterCount ? /*#__PURE__*/_jsx(CharacterCount, {
      id: characterCountId,
      length: lengthToShow,
      maxLength: maxLength
    }) : null,
    onChange: handleChange
  }));
});
export default CharacterCountInput;